import { distace } from "../movenet/params";

// Camera intrinsics for real-world conversion
const CAMERA_INTRINSICS = {
  fx: 743.7338239,
  fy: 740.21612805,
  cx: 598.68043092,
  cy: 960.06836463,
};

// Multiplier for forward/backward sway values to amplify the readings
const FORWARD_BACKWARD_MULTIPLIER = 5; // Adjust this value to get desired scale

// Multiplier for left/right (sideways) sway values
const SIDEWAYS_MULTIPLIER = 1; // Lower multiplier for sideways movement

// Blaze pose keypoints
const LEFT_SHOULDER_IDX = 11;
const RIGHT_SHOULDER_IDX = 12;
const LEFT_HIP_IDX = 23;
const RIGHT_HIP_IDX = 24;

// Thresholds for movement classification (dimensionless ratios)
const YAW_THRESHOLD = 0.15;        // 15% edge asymmetry = rotation detected
const DRIFT_THRESHOLD = 0.08;      // 8% shoulder center shift = lateral drift
const SYMMETRY_THRESHOLD = 0.10;   // 10% edge difference allowed for forward/back

// Utility function to clamp ratios to prevent extreme values from noise
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * Tracks shoulder sway (forward/backward and sideways) using pose keypoints.
 * Stores reference frame and calculates sway relative to it.
 * @param {mCTSIB} context - The mCTSIB class instance (for state).
 * @param {Array} poses - Array of detected poses from MoveNet.
 * @returns {Object} - Sway metrics for current frame.
 */
export function recordShoulderSway(context, poses) {
  if (!poses || poses.length === 0) return null;

  // Use first pose (single person)
  const pose = poses[0];
  const leftShoulder = pose.keypoints[LEFT_SHOULDER_IDX];
  const rightShoulder = pose.keypoints[RIGHT_SHOULDER_IDX];
  const leftHip = pose.keypoints[LEFT_HIP_IDX];
  const rightHip = pose.keypoints[RIGHT_HIP_IDX];

  if (
    !leftShoulder ||
    !rightShoulder ||
    !leftHip ||
    !rightHip ||
    leftShoulder.score < 0.25 ||
    rightShoulder.score < 0.25 ||
    leftHip.score < 0.25 ||
    rightHip.score < 0.25
  ) {
    return null;
  }

  // Convert normalized coordinates to pixel coordinates
  // Keypoints from MoveNet are normalized (0-1), so multiply by video dimensions
  const videoWidth = context.camera.video.videoWidth;
  const videoHeight = context.camera.video.videoHeight;

  // Check if keypoints are already in pixel coordinates or normalized
  // MoveNet typically returns normalized (0-1), but BlazePose might return pixels
  // If values are > 1, they're likely already in pixels
  // Check both shoulders AND hips to avoid coordinate mishaps
  const isNormalized =
    leftShoulder.x <= 1 &&
    leftShoulder.y <= 1 &&
    rightShoulder.x <= 1 &&
    rightShoulder.y <= 1 &&
    leftHip.x <= 1 &&
    leftHip.y <= 1 &&
    rightHip.x <= 1 &&
    rightHip.y <= 1;

  // Convert to pixel coordinates based on whether they're normalized or not
  const leftShoulderPixel = isNormalized
    ? {
        x: leftShoulder.x * videoWidth,
        y: leftShoulder.y * videoHeight,
      }
    : {
        x: leftShoulder.x,
        y: leftShoulder.y,
      };

  const rightShoulderPixel = isNormalized
    ? {
        x: rightShoulder.x * videoWidth,
        y: rightShoulder.y * videoHeight,
      }
    : {
        x: rightShoulder.x,
        y: rightShoulder.y,
      };

  // Convert hip keypoints to pixel coordinates
  const leftHipPixel = isNormalized
    ? {
        x: leftHip.x * videoWidth,
        y: leftHip.y * videoHeight,
      }
    : {
        x: leftHip.x,
        y: leftHip.y,
      };

  const rightHipPixel = isNormalized
    ? {
        x: rightHip.x * videoWidth,
        y: rightHip.y * videoHeight,
      }
    : {
        x: rightHip.x,
        y: rightHip.y,
      };

  // Debug: Log video dimensions and keypoint values on first frame
  if (context.sway_frame_count === 0 && !context.reference_captured) {
    console.log("Video dimensions:", { videoWidth, videoHeight });
    console.log("Raw keypoint values:", {
      leftShoulder: {
        x: leftShoulder.x,
        y: leftShoulder.y,
        score: leftShoulder.score,
        xType: leftShoulder.x > 1 ? "pixels" : "normalized",
      },
      rightShoulder: {
        x: rightShoulder.x,
        y: rightShoulder.y,
        score: rightShoulder.score,
        xType: rightShoulder.x > 1 ? "pixels" : "normalized",
      },
      isNormalized: isNormalized ? "Yes (0-1)" : "No (likely pixels)",
      calculatedPixels: {
        left: {
          x: leftShoulderPixel.x.toFixed(1),
          y: leftShoulderPixel.y.toFixed(1),
        },
        right: {
          x: rightShoulderPixel.x.toFixed(1),
          y: rightShoulderPixel.y.toFixed(1),
        },
      },
    });
  }

  // Validate pixel coordinates are reasonable (allow some margin for edge cases)
  const maxX = videoWidth * 1.5; // Allow 50% margin
  const maxY = videoHeight * 1.5;
  if (
    leftShoulderPixel.x < -videoWidth * 0.1 ||
    leftShoulderPixel.x > maxX ||
    rightShoulderPixel.x < -videoWidth * 0.1 ||
    rightShoulderPixel.x > maxX ||
    leftShoulderPixel.y < -videoHeight * 0.1 ||
    leftShoulderPixel.y > maxY ||
    rightShoulderPixel.y < -videoHeight * 0.1 ||
    rightShoulderPixel.y > maxY
  ) {
    console.warn("Invalid pixel coordinates detected:", {
      leftShoulderPixel: {
        x: leftShoulderPixel.x.toFixed(1),
        y: leftShoulderPixel.y.toFixed(1),
      },
      rightShoulderPixel: {
        x: rightShoulderPixel.x.toFixed(1),
        y: rightShoulderPixel.y.toFixed(1),
      },
      videoWidth,
      videoHeight,
      rawLeft: { x: leftShoulder.x, y: leftShoulder.y },
      rawRight: { x: rightShoulder.x, y: rightShoulder.y },
      isNormalized,
    });
    // Don't return null - try to use the values anyway, but clamp them
    leftShoulderPixel.x = Math.max(
      0,
      Math.min(leftShoulderPixel.x, videoWidth)
    );
    leftShoulderPixel.y = Math.max(
      0,
      Math.min(leftShoulderPixel.y, videoHeight)
    );
    rightShoulderPixel.x = Math.max(
      0,
      Math.min(rightShoulderPixel.x, videoWidth)
    );
    rightShoulderPixel.y = Math.max(
      0,
      Math.min(rightShoulderPixel.y, videoHeight)
    );
  }

  // Check if we need to capture reference frame (only when ready_to_capture_reference is true)
  // This flag is set in mCTSIB.js when entering assessment_In_progress state
  if (context.ready_to_capture_reference && !context.reference_captured) {
    // Initialize reference frame collection on first call
    if (!context.reference_frames_collected) {
      context.reference_frames_collected = [];
      context.reference_collection_count = 0;
    }

    // Collect frames for averaging immediately (no waiting period)
    // Collect 5 frames with good confidence for a stable reference
    const frames_to_collect = 5;

    // Only collect frames with good keypoint confidence (all 4 keypoints)
    if (
      leftShoulder.score >= 0.5 &&
      rightShoulder.score >= 0.5 &&
      leftHip.score >= 0.5 &&
      rightHip.score >= 0.5
    ) {
      context.reference_frames_collected.push({
        leftShoulder: { ...leftShoulderPixel },
        rightShoulder: { ...rightShoulderPixel },
        leftHip: { ...leftHipPixel },
        rightHip: { ...rightHipPixel },
      });
      context.reference_collection_count++;
    }

    // Once we've collected enough frames, calculate the average reference
    if (context.reference_collection_count >= frames_to_collect) {
      const collectedCount = context.reference_frames_collected.length;

      // Need at least 3 good frames
      if (collectedCount >= 3) {
        // Sum all keypoint positions for averaging
        let sumLeftShoulderX = 0, sumLeftShoulderY = 0;
        let sumRightShoulderX = 0, sumRightShoulderY = 0;
        let sumLeftHipX = 0, sumLeftHipY = 0;
        let sumRightHipX = 0, sumRightHipY = 0;

        for (const frame of context.reference_frames_collected) {
          sumLeftShoulderX += frame.leftShoulder.x;
          sumLeftShoulderY += frame.leftShoulder.y;
          sumRightShoulderX += frame.rightShoulder.x;
          sumRightShoulderY += frame.rightShoulder.y;
          sumLeftHipX += frame.leftHip.x;
          sumLeftHipY += frame.leftHip.y;
          sumRightHipX += frame.rightHip.x;
          sumRightHipY += frame.rightHip.y;
        }

        // Calculate averaged reference positions
        context.sway_reference_left = {
          x: sumLeftShoulderX / collectedCount,
          y: sumLeftShoulderY / collectedCount,
        };
        context.sway_reference_right = {
          x: sumRightShoulderX / collectedCount,
          y: sumRightShoulderY / collectedCount,
        };
        context.ref_left_hip = {
          x: sumLeftHipX / collectedCount,
          y: sumLeftHipY / collectedCount,
        };
        context.ref_right_hip = {
          x: sumRightHipX / collectedCount,
          y: sumRightHipY / collectedCount,
        };

        // Calculate torso quadrilateral reference metrics
        context.ref_shoulder_width = Math.hypot(
          context.sway_reference_left.x - context.sway_reference_right.x,
          context.sway_reference_left.y - context.sway_reference_right.y
        );
        context.ref_hip_width = Math.hypot(
          context.ref_left_hip.x - context.ref_right_hip.x,
          context.ref_left_hip.y - context.ref_right_hip.y
        );
        context.ref_left_edge = Math.hypot(
          context.sway_reference_left.x - context.ref_left_hip.x,
          context.sway_reference_left.y - context.ref_left_hip.y
        );
        context.ref_right_edge = Math.hypot(
          context.sway_reference_right.x - context.ref_right_hip.x,
          context.sway_reference_right.y - context.ref_right_hip.y
        );

        // Debug: Log reference frame info
        const ref_mid_x =
          (context.sway_reference_left.x + context.sway_reference_right.x) / 2;
        const ref_mid_y =
          (context.sway_reference_left.y + context.sway_reference_right.y) / 2;
        console.log(
          "Reference frame captured (immediately on assessment start):",
          {
            leftShoulder: {
              x: context.sway_reference_left.x.toFixed(1),
              y: context.sway_reference_left.y.toFixed(1),
            },
            rightShoulder: {
              x: context.sway_reference_right.x.toFixed(1),
              y: context.sway_reference_right.y.toFixed(1),
            },
            leftHip: {
              x: context.ref_left_hip.x.toFixed(1),
              y: context.ref_left_hip.y.toFixed(1),
            },
            rightHip: {
              x: context.ref_right_hip.x.toFixed(1),
              y: context.ref_right_hip.y.toFixed(1),
            },
            midpoint: { x: ref_mid_x.toFixed(1), y: ref_mid_y.toFixed(1) },
            torsoMetrics: {
              shoulderWidth: context.ref_shoulder_width.toFixed(2),
              hipWidth: context.ref_hip_width.toFixed(2),
              leftEdge: context.ref_left_edge.toFixed(2),
              rightEdge: context.ref_right_edge.toFixed(2),
            },
            videoDimensions: { width: videoWidth, height: videoHeight },
            framesAveraged: collectedCount,
            note:
              context.ref_shoulder_width > videoWidth
                ? "WARNING: Shoulder width exceeds video width - coordinate issue!"
                : "OK",
          }
        );

        // Reset frame count and data arrays
        context.sway_frame_count = 0;
        context.sway_x_data = [];
        context.sway_y_data = [];
        // Store (0, 0) for the reference frame itself
        context.sway_x_data.push(0);
        context.sway_y_data.push(0);

        // Mark that reference has been captured
        context.reference_captured = true;
        context.ready_to_capture_reference = false;
        context.reference_frames_collected = null;
        context.reference_collection_count = 0;

        return {
          sway_sideways_ft: 0,
          sway_forward_backward_ft: 0,
          frame: 0,
        };
      }
    }

    // Still collecting frames for reference - don't return data yet
    return null;
  }

  // If reference hasn't been captured yet, return null
  if (!context.sway_reference_left || !context.sway_reference_right) {
    return null;
  }

  // Increment frame count after reference is captured
  context.sway_frame_count++;

  // Calculate sway relative to reference frame
  // Sideways movement: use hip-weighted torso center (hips are more stable than shoulders)
  const ref_sh_mid_x =
    (context.sway_reference_left.x + context.sway_reference_right.x) / 2;
  const ref_hip_mid_x =
    (context.ref_left_hip.x + context.ref_right_hip.x) / 2;

  const curr_sh_mid_x = (leftShoulderPixel.x + rightShoulderPixel.x) / 2;
  const curr_hip_mid_x = (leftHipPixel.x + rightHipPixel.x) / 2;

  // Shoulder-weighted torso center (80% shoulder, 20% hip - shoulders show more sway)
  const ref_torso_x = 0.8 * ref_sh_mid_x + 0.2 * ref_hip_mid_x;
  const curr_torso_x = 0.8 * curr_sh_mid_x + 0.2 * curr_hip_mid_x;

  const sway_sideways_pixels = curr_torso_x - ref_torso_x;

  // Calculate current torso metrics for yaw detection and forward/backward
  const curr_shoulder_width = Math.hypot(
    leftShoulderPixel.x - rightShoulderPixel.x,
    leftShoulderPixel.y - rightShoulderPixel.y
  );
  const curr_hip_width = Math.hypot(
    leftHipPixel.x - rightHipPixel.x,
    leftHipPixel.y - rightHipPixel.y
  );
  const curr_left_edge = Math.hypot(
    leftShoulderPixel.x - leftHipPixel.x,
    leftShoulderPixel.y - leftHipPixel.y
  );
  const curr_right_edge = Math.hypot(
    rightShoulderPixel.x - rightHipPixel.x,
    rightShoulderPixel.y - rightHipPixel.y
  );

  // Calculate clamped ratios (0.5-1.5 range to filter noise)
  const S_ratio = clamp(curr_shoulder_width / context.ref_shoulder_width, 0.5, 1.5);
  const H_ratio = clamp(curr_hip_width / context.ref_hip_width, 0.5, 1.5);
  const L_ratio = clamp(curr_left_edge / context.ref_left_edge, 0.5, 1.5);
  const R_ratio = clamp(curr_right_edge / context.ref_right_edge, 0.5, 1.5);

  // Yaw detection with hysteresis (prevents flip-flopping at threshold)
  const yawScore = Math.abs(L_ratio - R_ratio);
  const YAW_ENTER_THRESHOLD = 0.15; // Enter yaw state at this level
  const YAW_EXIT_THRESHOLD = 0.10;  // Exit yaw state at this level

  if (context.yawActive == null) context.yawActive = false;
  if (!context.yawActive && yawScore > YAW_ENTER_THRESHOLD) context.yawActive = true;
  if (context.yawActive && yawScore < YAW_EXIT_THRESHOLD) context.yawActive = false;

  const isYawDetected = context.yawActive;

  // Forward/backward calculation using shoulder-weighted width change
  // Shoulders weighted 70%, hips 30% - because backward lean moves shoulders more than hips
  // fbScore is positive when leaning forward (widths increase), negative when leaning back
  const fbScore = 0.7 * (S_ratio - 1) + 0.3 * (H_ratio - 1);

  // Convert to inches
  const sway_sideways_in =
    ((sway_sideways_pixels * distace.patientHeelToIpad * 3.281 * 12) /
      CAMERA_INTRINSICS.fx) *
    SIDEWAYS_MULTIPLIER;

  // Compute stability weight based on yaw and shoulder confidence
  // This blends Y values instead of hard freezing/zeroing
  const yawWeight = clamp(1 - (yawScore / YAW_ENTER_THRESHOLD), 0, 1); // 1 = stable, 0 = full yaw
  const minShoulderScore = Math.min(leftShoulder.score, rightShoulder.score);
  const confWeight = clamp((minShoulderScore - 0.4) / 0.4, 0, 1); // 1 = high conf, 0 = low conf
  const stabilityWeight = yawWeight * confWeight;

  // Compute raw Y candidate
  const yCandidate =
    ((fbScore * context.ref_shoulder_width * distace.patientHeelToIpad * 3.281 * 12) /
      CAMERA_INTRINSICS.fx) *
    FORWARD_BACKWARD_MULTIPLIER;

  // Blend Y based on stability instead of hard freeze/zero
  // When yaw detected, decay toward 0 instead of snapping
  let sway_forward_backward_in;
  if (isYawDetected) {
    // Decay toward 0 (not instant snap)
    sway_forward_backward_in = (context.prev_raw_y ?? 0) * 0.85;
  } else {
    // Blend candidate with previous based on stability
    sway_forward_backward_in =
      stabilityWeight * yCandidate + (1 - stabilityWeight) * (context.prev_raw_y ?? 0);
  }

  // Spike rejection: clamp per-frame velocity before EMA
  // At 25-30 FPS, a patient cannot physically move much in one frame
  // This kills single-frame pose glitches (bad shoulder detection, occlusion, etc.)
  const MAX_X_STEP_IN = 0.5; // Max sideways change per frame (inches) - tightened
  const MAX_Y_STEP_IN = 0.5; // Max forward/backward change per frame (after multiplier) - tightened

  if (context.prev_raw_x == null) context.prev_raw_x = sway_sideways_in;
  if (context.prev_raw_y == null) context.prev_raw_y = sway_forward_backward_in;

  const deltaX = sway_sideways_in - context.prev_raw_x;
  const deltaY = sway_forward_backward_in - context.prev_raw_y;

  // Clamp spikes to max step size
  let clamped_sideways_in = sway_sideways_in;
  let clamped_forward_backward_in = sway_forward_backward_in;

  if (Math.abs(deltaX) > MAX_X_STEP_IN) {
    clamped_sideways_in = context.prev_raw_x + Math.sign(deltaX) * MAX_X_STEP_IN;
  }
  if (Math.abs(deltaY) > MAX_Y_STEP_IN) {
    clamped_forward_backward_in = context.prev_raw_y + Math.sign(deltaY) * MAX_Y_STEP_IN;
  }

  context.prev_raw_x = clamped_sideways_in;
  context.prev_raw_y = clamped_forward_backward_in;

  // Apply EMA smoothing (much better than 2-frame moving average)
  // Alpha 0.5 = fast response (~2 frames to 63%) - spike rejection handles glitches
  // At 25 FPS: ~80ms to reach 63% of movement
  const EMA_ALPHA = 0.5;

  if (context.sway_ema_x == null) context.sway_ema_x = clamped_sideways_in;
  if (context.sway_ema_y == null) context.sway_ema_y = clamped_forward_backward_in;

  context.sway_ema_x = EMA_ALPHA * context.sway_ema_x + (1 - EMA_ALPHA) * clamped_sideways_in;
  context.sway_ema_y = EMA_ALPHA * context.sway_ema_y + (1 - EMA_ALPHA) * clamped_forward_backward_in;

  const smoothX = context.sway_ema_x;
  const smoothY = context.sway_ema_y;

  // Validate and filter values
  // For the first few frames after reference, allow larger values (patient might still be settling)
  // After that, use more strict filtering
  const isEarlyFrame = context.sway_frame_count <= 10;
  const MAX_SWAY_IN_EARLY = 24.0; // Allow up to 24 inches in early frames (in case of initial offset)
  const MAX_SWAY_IN_NORMAL = 12.0; // Normal max sway is 12 inches
  const MAX_SWAY_IN = isEarlyFrame ? MAX_SWAY_IN_EARLY : MAX_SWAY_IN_NORMAL;

  let final_sideways_in = smoothX;
  let final_forward_backward_in = smoothY;

  // Only reject if value is impossibly large (likely calculation error)
  // Don't clamp to max - just filter out obvious errors
  // Scale the threshold based on multiplier for Y-axis (forward/backward)
  const ABSOLUTE_MAX_SWAY_IN = 48.0; // Absolute maximum - anything larger is definitely wrong
  const ABSOLUTE_MAX_SWAY_IN_Y =
    ABSOLUTE_MAX_SWAY_IN * FORWARD_BACKWARD_MULTIPLIER; // Scale for Y-axis
  if (Math.abs(smoothX) > ABSOLUTE_MAX_SWAY_IN) {
    // If value is impossibly large, use previous value or 0
    final_sideways_in =
      context.sway_x_data.length > 0
        ? context.sway_x_data[context.sway_x_data.length - 1]
        : 0;
    console.warn(
      `Rejected impossibly large sideways value: ${smoothX.toFixed(
        2
      )} inches`
    );
  }

  if (Math.abs(smoothY) > ABSOLUTE_MAX_SWAY_IN_Y) {
    final_forward_backward_in =
      context.sway_y_data.length > 0
        ? context.sway_y_data[context.sway_y_data.length - 1]
        : 0;
    console.warn(
      `Rejected impossibly large forward/backward value: ${smoothY.toFixed(
        2
      )} inches (threshold: ${ABSOLUTE_MAX_SWAY_IN_Y.toFixed(2)})`
    );
  }

  // Jump detection - only apply after first few frames and be less aggressive
  // This helps smooth out sudden pose detection errors
  if (context.sway_x_data.length > 0 && !isEarlyFrame) {
    const lastX = context.sway_x_data[context.sway_x_data.length - 1];
    const lastY = context.sway_y_data[context.sway_y_data.length - 1];
    const jumpX = Math.abs(final_sideways_in - lastX);
    const jumpY = Math.abs(final_forward_backward_in - lastY);

    // If jump is more than 4 inches in one frame (after early frames), smooth it
    // Scale the Y-axis threshold based on multiplier
    const JUMP_THRESHOLD_X = 4.0;
    const JUMP_THRESHOLD_Y = 4.0 * FORWARD_BACKWARD_MULTIPLIER;
    if (jumpX > JUMP_THRESHOLD_X) {
      // Interpolate between last and current (80% last, 20% current) to smooth jump
      final_sideways_in = lastX * 0.8 + final_sideways_in * 0.2;
    }
    if (jumpY > JUMP_THRESHOLD_Y) {
      final_forward_backward_in = lastY * 0.8 + final_forward_backward_in * 0.2;
    }
  }

  // Deadband with decay: gradually reduce tiny movements instead of hard zero
  // This prevents jarring transitions when entering/exiting deadband zone
  const DEADBAND_X = 0.25; // 0.25 inches deadband for sideways
  const DEADBAND_Y = 0.25 * FORWARD_BACKWARD_MULTIPLIER; // Scaled for forward/backward
  const DEADBAND_FLOOR = 0.05; // Below this, snap to zero

  if (Math.abs(final_sideways_in) < DEADBAND_X) {
    final_sideways_in *= 0.8; // Decay by 20%
    if (Math.abs(final_sideways_in) < DEADBAND_FLOOR) final_sideways_in = 0;
  }
  if (Math.abs(final_forward_backward_in) < DEADBAND_Y) {
    final_forward_backward_in *= 0.8; // Decay by 20%
    if (Math.abs(final_forward_backward_in) < DEADBAND_FLOOR * FORWARD_BACKWARD_MULTIPLIER) {
      final_forward_backward_in = 0;
    }
  }

  // Debug logging (remove in production)
  if (context.sway_frame_count <= 5) {
    const curr_mid_x = (leftShoulderPixel.x + rightShoulderPixel.x) / 2;
    const curr_mid_y = (leftShoulderPixel.y + rightShoulderPixel.y) / 2;
    const ref_mid_x =
      (context.sway_reference_left.x + context.sway_reference_right.x) / 2;
    const ref_mid_y =
      (context.sway_reference_left.y + context.sway_reference_right.y) / 2;
    console.log(`Frame ${context.sway_frame_count}:`, {
      currentMidpoint: { x: curr_mid_x.toFixed(1), y: curr_mid_y.toFixed(1) },
      referenceMidpoint: { x: ref_mid_x.toFixed(1), y: ref_mid_y.toFixed(1) },
      torsoRatios: {
        S: S_ratio.toFixed(3),
        H: H_ratio.toFixed(3),
        L: L_ratio.toFixed(3),
        R: R_ratio.toFixed(3),
      },
      yawScore: yawScore.toFixed(3),
      isYawDetected,
      fbScore: fbScore.toFixed(3),
      rawInches: {
        x: sway_sideways_in.toFixed(3),
        y: sway_forward_backward_in.toFixed(3),
      },
      smoothed: { x: smoothX.toFixed(3), y: smoothY.toFixed(3) },
      final: {
        x: final_sideways_in.toFixed(3),
        y: final_forward_backward_in.toFixed(3),
      },
    });
  }

  // Convert to feet for return value (graph might expect feet)
  const sway_sideways_ft = final_sideways_in / 12;
  const sway_forward_backward_ft = final_forward_backward_in / 12;

  // Store for visualization (in inches for graph display)
  if (
    Number.isFinite(final_sideways_in) &&
    Number.isFinite(final_forward_backward_in)
  ) {
    context.sway_x_data.push(final_sideways_in);
    // Invert y-axis: positive width change (forward) should be negative in graph (backward)
    // So we negate the forward_backward value
    context.sway_y_data.push(-final_forward_backward_in);
  }

  return {
    sway_sideways_ft,
    sway_forward_backward_ft: -sway_forward_backward_ft, // Invert for display (forward = negative)
    frame: context.sway_frame_count,
  };
}
